import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { ExistsAspect } from '@adornis/filter/aspect-schemas/ExistsAspect.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import {
  CourseEnrollmentManager,
  type EnrollmentDataSource,
} from '@nsc/simple-lms/client/course-enrollment-manager.js';
import { LMSEnrollmentStatus } from '@nsc/simple-lms/entities/LMSCourseEnrollment.js';
import { html, type TemplateResult } from 'lit';
import type { CourseEnrollmentPage } from '../../api/course-enrollment-page.js';
import { getCourseEnrollmentsPage } from '../../api/course-enrollment-page.js';
import { SmartTeamsFieldEngineer } from '../../db/SmartTeamsFieldEngineer.js';
import { formatDate } from '../date-format.js';

/**
 * Field Engineer picker for course enrollment.
 * Uses SmartTeamsFieldEngineer entity picker and extracts userId for enrollment.
 */
const FieldEngineerEnrollmentPicker = ({ onUserPicked }: { onUserPicked: (userId: string) => void }): TemplateResult =>
  html`${EntityPickerInput<typeof SmartTeamsFieldEngineer>({
    class: SmartTeamsFieldEngineer,
    filter: new AdornisFilter({ searchFields: ['name'], aspects: [new ExistsAspect({ fieldName: 'userId' })] }),
    selectionSet: { _id: 1, name: 1, userId: 1, firstLanguage: 1, secondLanguage: 1 },
    placeholder: 'Assign field engineers',
    emptyState: 'No field engineers available',
    renderString: (fe: SmartTeamsFieldEngineer) => {
      const languages = [fe.firstLanguage, fe.secondLanguage].filter(Boolean).join(', ');
      if (languages) return `${fe.name ?? ''} (${languages})`;
      return fe.name ?? '';
    },
    onEntityPicked: fe => {
      if (fe?.userId) {
        onUserPicked(fe.userId);
      }
    },
  })}`;

/**
 * Server-side data source: one query joins each enrollment to its field engineer (name / email /
 * languages), searches by name or email in MongoDB, and paginates — so the table renders without the
 * per-row FE lookups that used to fetch the entire field-engineer list for every row.
 */
const fieldEngineerDataSource: EnrollmentDataSource = {
  async loadPage({ courseId, search, skip, limit }) {
    const pageData = (await getCourseEnrollmentsPage(courseId, search, skip, limit)({
      _id: 1,
      total: 1,
      rows: {
        _id: 1,
        userId: 1,
        feName: 1,
        feEmail: 1,
        feLanguages: 1,
        status: 1,
        lockedAt: 1,
        firstPassedAt: 1,
        hasCompletedAttempt: 1,
        completedAttempts: { scorePercent: 1, passed: 1, ip: 1 },
      },
    })) as CourseEnrollmentPage;

    return {
      total: pageData.total ?? 0,
      rows: (pageData.rows ?? []).map(row => ({
        enrollmentId: row._id,
        userId: row.userId ?? null,
        // Field engineers (with languages) get their name up front; dangling links fall back to the
        // manager's lazy user-name lookup by leaving `name` null.
        name: row.feName ? (row.feLanguages ? `${row.feName} (${row.feLanguages})` : row.feName) : null,
        subtitle: row.feEmail ?? null,
        status: (row.status as LMSEnrollmentStatus | null) ?? LMSEnrollmentStatus.ASSIGNED,
        lockedAt: row.lockedAt ?? null,
        completedAttempts: (row.completedAttempts ?? []).map(a => ({ scorePercent: a.scorePercent, passed: a.passed, ip: a.ip })),
        firstPassedAt: row.firstPassedAt ?? null,
        hasCompletedAttempt: row.hasCompletedAttempt ?? false,
      })),
    };
  },
};

/**
 * Course enrollment manager customized for SmartTeams.
 * Uses the FieldEngineer picker for enrollment and a server-side data source for the table.
 */
export const FieldEngineerEnrollmentManager = stateful(
  ({ courseId }: { courseId: string }) =>
    html`${CourseEnrollmentManager({
      courseId,
      renderEnrollmentPicker: FieldEngineerEnrollmentPicker,
      dataSource: fieldEngineerDataSource,
      // Render the "First passed at" date as d/m/Y for consistency with the rest of the app.
      formatDate: date => formatDate(date) || '–',
    })}`,
);
